import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, ArrowDown } from 'lucide-react';

/**
 * Tile compacto del header del dashboard de Metricas.
 * Numero grande slate-900 + label uppercase tracking-wide + delta opcional
 * (chip cyan/rose con flechita) + sub-line opcional ("Y vencen esta semana").
 */
const KpiTile = ({
  label,
  value,
  sub = null,
  delta = null,            // numero entero. + verde, - rojo, 0/null sin chip
  deltaSuffix = '',        // sufijo opcional para el chip ej "vs sem pasada"
  icon: Icon = null,
  accent = 'slate',        // slate | cyan | amber | rose | emerald (color del icono y delta)
  onClick = null,
  loading = false,
}) => {
  const accentBg = {
    slate:   'bg-slate-100 text-slate-600',
    cyan:    'bg-cyan-50 text-cyan-600',
    amber:   'bg-amber-50 text-amber-600',
    rose:    'bg-rose-50 text-rose-600',
    emerald: 'bg-emerald-50 text-emerald-600',
  }[accent] || 'bg-slate-100 text-slate-600';

  const Wrapper = onClick ? motion.button : 'div';
  const wrapperProps = onClick
    ? { onClick, whileTap: { scale: 0.99 }, transition: { duration: 0.1 } }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={`relative bg-white rounded-lg border border-slate-200/70 p-4 text-left ${
        onClick ? 'hover:border-slate-300 transition-colors cursor-pointer' : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
          {label}
        </span>
        {Icon && (
          <div className={`w-7 h-7 rounded-md ${accentBg} flex items-center justify-center flex-shrink-0`}>
            <Icon size={14} strokeWidth={2} />
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-7 w-16 bg-slate-100 rounded skeleton" />
      ) : (
        <div className="text-2xl font-bold text-slate-900 tracking-tight tabular-nums leading-none">
          {value}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between min-h-[18px]">
        {sub && (
          <span className="text-[11px] text-slate-500 truncate">
            {sub}
          </span>
        )}
        {delta !== null && delta !== undefined && !loading && (
          <DeltaChip value={delta} suffix={deltaSuffix} />
        )}
      </div>
    </Wrapper>
  );
};

const DeltaChip = ({ value, suffix }) => {
  if (value === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-slate-500 tabular-nums">
        = {suffix}
      </span>
    );
  }
  const positive = value > 0;
  const Icon = positive ? ArrowUp : ArrowDown;
  const cls = positive
    ? 'bg-cyan-50 text-cyan-700'
    : 'bg-rose-50 text-rose-700';
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded ${cls} text-[10px] font-semibold tabular-nums`}>
      <Icon size={9} strokeWidth={2.5} />
      {Math.abs(value)}
      {suffix && <span className="ml-0.5 font-normal opacity-80">{suffix}</span>}
    </span>
  );
};

export default KpiTile;
